import { SagaIterator } from 'redux-saga';
import {
  call,
  put,
  select,
  takeEvery,
  all,
  takeLatest,
  delay,
} from 'redux-saga/effects';
import { PapiDeviation } from '@wix/da-types-papi';
import { selectors, withOffset } from '@wix/da-shared-react/pkg/Stream';
import { getPageSize } from '@wix/da-shared-react/pkg/publicSession';
import { flagEnabled } from '@wix/da-shared-react/pkg/redux/flags';
import { initializeThread } from '@wix/da-shared-react/pkg/CommentThread/actionCreators';
import { getCommentableTypeForDeviationType } from '@wix/da-shared-react/pkg/DeviationPage/redux/helpers';
import { ApiGetEndpoint, Pages } from '../../constants';
import {
  DaBrowseFacetedRequest,
  DaBrowseFacetedResponse,
} from '../../types/api';
import { apiGet } from '../helpers/xhr';
import { getNormalizedFacetedRequestParams } from '../../server/routes/pages/helpers';
import { getFacetStreamState, getItemsPerFetch } from '../selectors/stream';
import { initFacetData, fetchFacetStreamNext } from '../actions/facet';
import { AppState } from '../types/store';
import { getPage } from '../selectors/page';
import { getFacetStreamIdByPageAndRawPageType } from '../../utils';
import { biFetchMoreItems } from '../actions/bi';
import { getTagStreamState } from '../selectors/tags';
import { getNormalizedStreamItems } from '../helpers/normalize';

const FEED_COMMENTS_MAX_DEPTH = 1;
const FEED_COMMENTS_ITEMS_PER_FETCH = 10;

function* onFetchWithOffset({
  payload: { streamId },
}: ReturnType<typeof fetchFacetStreamNext>) {
  yield delay(200); // debounce

  const streamParams = yield select(selectors.getStreamParams, streamId);
  if (!streamParams || streamParams.type !== 'browsePage') {
    return;
  }

  yield put(biFetchMoreItems(streamId));

  // the request params of the tags api is different than the more general facet api
  // but the responses are the same, so we split the request into this function
  // and requestFacetWithNewOffset and have both return a DaBrowseFacetedResponse
  const currentPage = yield select(getPage);
  const result: DaBrowseFacetedResponse | false =
    currentPage === 'tag'
      ? yield call(requestTagsWithNewOffset, streamId)
      : yield call(requestFacetWithNewOffset, streamId);

  if (!result) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        errorMsg: "Can't load deviations",
      })
    );
    return;
  }

  const { hasMore = false, nextOffset } = result;
  const { items, entities } = getNormalizedStreamItems(result);
  yield put(
    withOffset.actions.fetchSuccess({
      streamId,
      items,
      entities,
      hasMore,
      nextOffset: nextOffset!,
    })
  );

  if (currentPage === Pages.Posts && result.deviations) {
    yield call(initializePostThreads, result.deviations);
  }
}

/** Make an api request for the next group of tagged items based on the offset of the previous request */
function* requestTagsWithNewOffset(streamId: string) {
  const streamState = yield select(getTagStreamState, streamId);
  const {
    offset,
    requestParams: { offset: oldOffset, page: oldPage, ...requestParams },
  }: ReturnType<typeof getTagStreamState> = streamState;

  const newParams: DaBrowseFacetedRequest = getNormalizedFacetedRequestParams({
    ...requestParams,
    init: false,
    offset,
  });

  const result: DaBrowseFacetedResponse = yield call(
    params => apiGet(ApiGetEndpoint.Tags, params),
    newParams
  );
  return result;
}

/** Make an api request for the next group of faceted items based on the offset of the previous request */
function* requestFacetWithNewOffset(streamId: string) {
  const {
    offset,
    facetRequest: { offset: oldOffset, page: oldPage, ...facetRequest },
  }: ReturnType<typeof getFacetStreamState> = yield select(
    getFacetStreamState,
    streamId
  );

  const requestParams: DaBrowseFacetedRequest = getNormalizedFacetedRequestParams(
    { init: false, ...facetRequest, offset }
  );

  const result: DaBrowseFacetedResponse = yield call(
    params => apiGet(ApiGetEndpoint.Faceted, params),
    requestParams
  );
  return result;
}

function* initFacetStream(action: ReturnType<typeof initFacetData>) {
  if (!action.payload) {
    return;
  }

  const { page, itemsPerFetch, pageSize } = yield select((state: AppState) => ({
    page: getPage(state),
    itemsPerFetch: getItemsPerFetch(state),
    pageSize: getPageSize(state),
  }));

  const {
    initData: { hasMore, nextOffset, estTotal, usedLimit = pageSize },
    requestParams: {
      page_type,
      page: pageOffset = 1,
      offset: requestOffset = (pageOffset - 1) * usedLimit,
    },
  } = action.payload;

  const streamId = getFacetStreamIdByPageAndRawPageType(page, page_type);
  const { items, itemType, entities } = getNormalizedStreamItems(
    action.payload.initData
  );
  const streamParams = {
    type: 'browsePage',
    initialOffset: requestOffset,
    pageSize: usedLimit,
    currentPage: Math.floor(requestOffset / usedLimit) + 1,
    totalPages: estTotal && Math.floor(estTotal / usedLimit) + 1,
    itemType,
  };

  yield put(
    withOffset.actions.initialize({
      streamId,
      itemsPerFetch,
      items,
      hasMore,
      hasLess: requestOffset > 0,
      prevOffset: Math.max(requestOffset - pageSize, 0),
      nextOffset: nextOffset || undefined,
      total: estTotal,
      streamParams,
      entities,
      fetchNextCallback: fetchFacetStreamNext,
    })
  );

  if (page === Pages.Posts && action.payload.initData.deviations) {
    yield call(initializePostThreads, action.payload.initData.deviations);
  }
}

function* initializePostThreads(deviations: PapiDeviation[]) {
  const postfeed_longer_comment_pages = yield select(
    flagEnabled,
    'postfeed_longer_comment_pages'
  );
  const commentThreads = deviations.map(deviation =>
    put(
      initializeThread({
        thread: {
          commentableItemId: deviation.deviationId,
          commentableTypeId: getCommentableTypeForDeviationType(
            deviation.typeId
          ),
          items: [],
          hasMore: !!deviation.stats?.comments,
          total: deviation.stats?.comments ?? 0,
          canPostComment: !!deviation.isCommentable,
        },
        maxDepth: FEED_COMMENTS_MAX_DEPTH,
        ...(postfeed_longer_comment_pages && {
          itemsPerFetch: FEED_COMMENTS_ITEMS_PER_FETCH,
        }),
      })
    )
  );
  yield all(commentThreads);
}

export default function* browseStreamsRootSaga(): SagaIterator {
  yield all([
    takeLatest(fetchFacetStreamNext, onFetchWithOffset),
    takeEvery(initFacetData, initFacetStream),
  ]);
}
